import { t } from 'i18next'
import moment from 'moment'

import { NTB_COLUMN_KEYS } from './ntbMetrics'

const IMPRESSION_SHARE_FAMILY_KEYS = [
  'impression_share',
  'lost_is_rank',
  'lost_is_budget'
] as const

/**
 * A sonda só pergunta se a chave veio na resposta, então qualquer objeto de
 * métricas serve — `Record<string, unknown>` excluiria as interfaces do domínio
 * (`ReportCampaigns` e afins), que não têm index signature.
 */
type MetricsSource = object | null | undefined

const hasMetricField = (source: MetricsSource, key: string) =>
  !!source && Object.hasOwn(source, key)

/** True when IS family keys are absent (entitlement off), not when values are null. */
export const isImpressionShareFamilyAbsent = (source: MetricsSource): boolean =>
  !source ||
  !IMPRESSION_SHARE_FAMILY_KEYS.some(key => hasMetricField(source, key))

/** Payload-only availability probe. Entitlement overrides stay at callers. */
export const getNewMetricsAvailability = (
  sources: MetricsSource[]
): NewMetricsAvailability => {
  const impressionShareAvailable = sources.some(
    source => !isImpressionShareFamilyAbsent(source)
  )

  return {
    impressionShare: impressionShareAvailable,
    lostIsRank: impressionShareAvailable,
    lostIsBudget: impressionShareAvailable,
    // coluna disponível quando a fonte traz o campo (mesmo null); célula sem dado vira traço
    newToBrandCustomers: sources.some(source =>
      hasMetricField(source, 'new_to_brand_customers')
    ),
    newToBrandRevenue: sources.some(source =>
      hasMetricField(source, 'new_to_brand_revenue')
    )
  }
}

// Janela de pedidos do snapshot NTB ([fim − 30d, fim]), no mesmo padrão de
// data dos badges dos cards (common:patterns.dateMonth)
export const getNtbOrdersWindow = (
  endDate: string | Date | null | undefined
): NtbOrdersWindow => {
  const end = moment(endDate ?? undefined)
  if (!endDate || !end.isValid()) return null

  const pattern = t('common:patterns.dateMonth')

  return {
    checkpointStart: end.clone().subtract(30, 'days').format(pattern),
    checkpointEnd: end.format(pattern)
  }
}

// true quando ao menos uma coluna NTB está visível/selecionada no schema
export const hasVisibleNtbColumn = (
  visibleSchema: Record<string, unknown> | undefined | null
): boolean =>
  !!visibleSchema && NTB_COLUMN_KEYS.some(key => key in visibleSchema)
